import { useState } from 'react';
import SEO from '../../components/SEO';

export default function Contact() {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    company: '',
    subject: '',
    message: '',
    moringaPackageSize: '',
    moringaQuantity: '',
    moringaDelivery: '',
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');

  const isMoringaOrder = formData.subject === 'Order Pure Moringa Powder';

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus('idle');

    try {
      const subjectSelect = document.getElementById('subject') as HTMLSelectElement;
      const selectedSubject = subjectSelect.options[subjectSelect.selectedIndex].text;

      const submitData: Record<string, string> = {
        name: formData.name,
        email: formData.email,
        phone: formData.phone,
        company: formData.company,
        subject: selectedSubject,
        message: formData.message,
      };

      if (isMoringaOrder) {
        if (formData.moringaPackageSize) submitData['Package Size'] = formData.moringaPackageSize;
        if (formData.moringaQuantity) submitData['Quantity (packs)'] = formData.moringaQuantity;
        if (formData.moringaDelivery) submitData['Delivery Preference'] = formData.moringaDelivery;
      }

      const response = await fetch('https://readdy.ai/api/form/d4marp2t20jieso0q220', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: new URLSearchParams(submitData).toString(),
      });

      if (response.ok) {
        setSubmitStatus('success');
        setFormData({ name: '', email: '', phone: '', company: '', subject: '', message: '', moringaPackageSize: '', moringaQuantity: '', moringaDelivery: '' });
        setTimeout(() => setSubmitStatus('idle'), 6000);
      } else {
        setSubmitStatus('error');
      }
    } catch {
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  const siteUrl = import.meta.env.VITE_SITE_URL || 'https://example.com';

  const faqSchema = {
    "@context": "https://schema.org",
    "@graph": [
      {
        "@type": "ContactPage",
        "name": "Contact MY AVA Farms — Order Moringa Powder & BSF Farming Accra Ghana",
        "description": "Contact MY AVA Farms in Accra Ghana to order pure moringa powder, enquire about Black Soldier Fly farming, BSF training programs, and consultancy services.",
        "url": `${siteUrl}/contact`,
        "mainEntity": {
          "@type": "LocalBusiness",
          "name": "MY AVA Farms",
          "telephone": "+233233371378",
          "email": "myavagh@gmail.com",
          "address": {
            "@type": "PostalAddress",
            "streetAddress": "33 Kwame Nkrumah Ave, Adabraka",
            "addressLocality": "Accra",
            "addressCountry": "GH"
          },
          "openingHoursSpecification": [
            {
              "@type": "OpeningHoursSpecification",
              "dayOfWeek": ["Monday","Tuesday","Wednesday","Thursday","Friday","Saturday"],
              "opens": "09:00",
              "closes": "17:00"
            }
          ]
        }
      },
      {
        "@type": "BreadcrumbList",
        "itemListElement": [
          { "@type": "ListItem", "position": 1, "name": "Home", "item": siteUrl },
          { "@type": "ListItem", "position": 2, "name": "Contact", "item": `${siteUrl}/contact` }
        ]
      },
      {
        "@type": "FAQPage",
        "mainEntity": [
          {
            "@type": "Question",
            "name": "How do I order pure moringa powder?",
            "acceptedAnswer": {
              "@type": "Answer",
              "text": "Simply fill out our contact form, select 'Order Pure Moringa Powder' as the subject, choose your preferred package size and quantity, and we will confirm your order and arrange payment and delivery."
            }
          },
          {
            "@type": "Question",
            "name": "What sizes does the moringa powder come in?",
            "acceptedAnswer": {
              "@type": "Answer",
              "text": "Our pure moringa powder is available in 50g, 100g, 250g, and 1kg packs. Bulk and wholesale orders are also available on request."
            }
          },
          {
            "@type": "Question",
            "name": "What is Black Soldier Fly farming?",
            "acceptedAnswer": {
              "@type": "Answer",
              "text": "Black Soldier Fly farming is a sustainable method of converting organic waste into high-quality protein feed and organic fertilizer using BSF larvae. It supports circular economy practices."
            }
          },
          {
            "@type": "Question",
            "name": "How long does the training program take?",
            "acceptedAnswer": {
              "@type": "Answer",
              "text": "Our comprehensive BSF training programs range from 1-day intensive workshops to 4-week detailed courses, depending on your experience level and specific needs."
            }
          },
          {
            "@type": "Question",
            "name": "Do you provide ongoing support after training?",
            "acceptedAnswer": {
              "@type": "Answer",
              "text": "Yes, we offer continuous technical support, regular follow-ups, and expert consultancy services to ensure your BSF farming success."
            }
          },
          {
            "@type": "Question",
            "name": "What products do you export?",
            "acceptedAnswer": {
              "@type": "Answer",
              "text": "We export BSF larvae, dried BSF protein meal, organic fertilizer, pure moringa powder, and BSF farming equipment to countries across West Africa and beyond."
            }
          }
        ]
      }
    ]
  };

  return (
    <>
      <SEO
        title="Contact MY AVA Farms | Order Moringa Powder, Accra Ghana"
        description="Contact MY AVA Farms in Accra Ghana to order pure moringa powder, enquire about Black Soldier Fly farming, BSF training programs, and consultancy services. Call +233 23 337 1378 or email myavagh@gmail.com."
        keywords="order moringa powder Ghana, contact MY AVA Farms, BSF farming Accra contact, moringa powder Accra, Black Soldier Fly training Ghana, sustainable agriculture Ghana contact"
        ogImage="https://public.readdy.ai/ai/img_res/61755908f884f25891ad4678a9aee627.jpg"
        schema={faqSchema}
      />
      <div className="min-h-screen bg-white">

        {/* Hero */}
        <section className="relative py-20 overflow-hidden">
          <div
            className="absolute inset-0 bg-cover bg-center"
            style={{ backgroundImage: 'url(https://readdy.ai/api/search-image?query=Fresh%20green%20moringa%20leaves%20and%20powder%20on%20a%20clean%20surface%2C%20natural%20organic%20superfood%20from%20Ghana%2C%20warm%20earthy%20tones%2C%20professional%20product%20photography%2C%20sustainable%20African%20farm%20produce%2C%20bright%20natural%20lighting%20with%20soft%20shadows&width=1920&height=600&seq=contact-hero-moringa-2026&orientation=landscape)' }}
          >
            <div className="absolute inset-0 bg-gradient-to-r from-green-900/80 via-green-800/60 to-green-700/40"></div>
          </div>
          <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <span className="inline-flex items-center bg-amber-500/90 text-white text-xs font-semibold px-4 py-1.5 rounded-full uppercase tracking-widest mb-5">
              <i className="ri-leaf-line mr-2"></i>Now Accepting Moringa Orders
            </span>
            <h1 className="text-4xl md:text-6xl font-bold text-white mb-5">
              Contact MY AVA Farms – Order Moringa Powder &amp; BSF Farming Enquiries
            </h1>
            <p className="text-xl text-green-100 max-w-3xl mx-auto">
              Order our pure moringa powder, enquire about BSF farming products, or reach out about training and consultancy — we are here to help.
            </p>
          </div>
        </section>

        {/* Contact Body */}
        <section className="py-20 bg-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-14">

              {/* Form */}
              <div>
                <h2 className="text-3xl font-bold text-gray-900 mb-2">Send Us a Message</h2>
                <p className="text-gray-500 text-sm mb-8">Select <strong>Order Pure Moringa Powder</strong> below to specify your order details.</p>

                <form onSubmit={handleSubmit} className="space-y-6" data-readdy-form id="contact-form">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-2">Full Name *</label>
                      <input type="text" id="name" name="name" value={formData.name} onChange={handleInputChange} required
                        className="w-full px-4 py-3 border border-gray-200 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent text-sm" placeholder="Your full name" />
                    </div>
                    <div>
                      <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-2">Email Address *</label>
                      <input type="email" id="email" name="email" value={formData.email} onChange={handleInputChange} required
                        className="w-full px-4 py-3 border border-gray-200 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent text-sm" placeholder="your.email@example.com" />
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <label htmlFor="phone" className="block text-sm font-medium text-gray-700 mb-2">Phone Number</label>
                      <input type="tel" id="phone" name="phone" value={formData.phone} onChange={handleInputChange}
                        className="w-full px-4 py-3 border border-gray-200 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent text-sm" placeholder="+233 XX XXX XXXX" />
                    </div>
                    <div>
                      <label htmlFor="company" className="block text-sm font-medium text-gray-700 mb-2">Company / Organization</label>
                      <input type="text" id="company" name="company" value={formData.company} onChange={handleInputChange}
                        className="w-full px-4 py-3 border border-gray-200 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent text-sm" placeholder="Your company name" />
                    </div>
                  </div>

                  {/* Subject */}
                  <div>
                    <label htmlFor="subject" className="block text-sm font-medium text-gray-700 mb-2">Subject *</label>
                    <select id="subject" name="subject" value={formData.subject} onChange={handleInputChange} required
                      className="w-full px-4 py-3 border border-gray-200 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent text-sm cursor-pointer">
                      <option value="">Select a subject</option>
                      <optgroup label="Moringa Powder">
                        <option value="Order Pure Moringa Powder">🌿 Order Pure Moringa Powder</option>
                        <option value="Moringa Wholesale Enquiry">Moringa Wholesale Enquiry</option>
                      </optgroup>
                      <optgroup label="BSF Products & Services">
                        <option value="BSF Training Programs">BSF Training Programs</option>
                        <option value="Product Catalog">Product Catalog</option>
                        <option value="Consultancy Services">Consultancy Services</option>
                        <option value="Export Services">Export Services</option>
                        <option value="Partnership Opportunities">Partnership Opportunities</option>
                        <option value="Technical Support">Technical Support</option>
                      </optgroup>
                      <option value="Other">Other</option>
                    </select>
                  </div>

                  {/* Moringa-specific fields — shown conditionally */}
                  {isMoringaOrder && (
                    <div className="bg-amber-50 border border-amber-200 rounded-xl p-5 space-y-5">
                      <div className="flex items-center gap-2 mb-1">
                        <i className="ri-leaf-line text-amber-600 text-lg"></i>
                        <p className="text-sm font-semibold text-amber-800">Moringa Order Details</p>
                      </div>
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                        <div>
                          <label htmlFor="moringaPackageSize" className="block text-sm font-medium text-gray-700 mb-2">Package Size *</label>
                          <select id="moringaPackageSize" name="moringaPackageSize" value={formData.moringaPackageSize} onChange={handleInputChange} required={isMoringaOrder}
                            className="w-full px-4 py-3 border border-amber-200 bg-white rounded-lg focus:ring-2 focus:ring-amber-400 focus:border-transparent text-sm cursor-pointer">
                            <option value="">Select size</option>
                            <option value="50g">50g pack</option>
                            <option value="100g">100g pack</option>
                            <option value="250g">250g pack</option>
                            <option value="1kg">1kg pack</option>
                            <option value="Bulk / Wholesale">Bulk / Wholesale</option>
                          </select>
                        </div>
                        <div>
                          <label htmlFor="moringaQuantity" className="block text-sm font-medium text-gray-700 mb-2">Number of Packs *</label>
                          <input type="number" id="moringaQuantity" name="moringaQuantity" value={formData.moringaQuantity} onChange={handleInputChange}
                            min="1" required={isMoringaOrder}
                            className="w-full px-4 py-3 border border-amber-200 bg-white rounded-lg focus:ring-2 focus:ring-amber-400 focus:border-transparent text-sm" placeholder="e.g. 5" />
                        </div>
                      </div>
                      <div>
                        <label htmlFor="moringaDelivery" className="block text-sm font-medium text-gray-700 mb-2">Delivery Preference *</label>
                        <select id="moringaDelivery" name="moringaDelivery" value={formData.moringaDelivery} onChange={handleInputChange} required={isMoringaOrder}
                          className="w-full px-4 py-3 border border-amber-200 bg-white rounded-lg focus:ring-2 focus:ring-amber-400 focus:border-transparent text-sm cursor-pointer">
                          <option value="">Select delivery option</option>
                          <option value="Pickup from farm (Accra)">Pickup from farm (Accra)</option>
                          <option value="Delivery within Accra">Delivery within Accra</option>
                          <option value="Delivery to other region in Ghana">Delivery to other region in Ghana</option>
                          <option value="International export">International export</option>
                        </select>
                      </div>
                    </div>
                  )}

                  <div>
                    <label htmlFor="message" className="block text-sm font-medium text-gray-700 mb-2">Message *</label>
                    <textarea id="message" name="message" value={formData.message} onChange={handleInputChange} required
                      rows={5} maxLength={500}
                      className="w-full px-4 py-3 border border-gray-200 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent text-sm resize-none"
                      placeholder={isMoringaOrder ? 'Any special requests, dietary requirements, or questions about your moringa order...' : 'Tell us about your project, questions, or how we can help you...'}
                    ></textarea>
                    <p className="text-xs text-gray-400 mt-1 text-right">{formData.message.length}/500</p>
                  </div>

                  {submitStatus === 'success' && (
                    <div className="bg-green-50 border border-green-200 text-green-800 px-4 py-3 rounded-lg flex items-center gap-2">
                      <i className="ri-check-line text-lg shrink-0"></i>
                      {isMoringaOrder
                        ? 'Your moringa order has been received! We will contact you shortly to confirm and arrange delivery.'
                        : 'Thank you for your message! We will get back to you soon.'}
                    </div>
                  )}

                  {submitStatus === 'error' && (
                    <div className="bg-red-50 border border-red-200 text-red-800 px-4 py-3 rounded-lg flex items-center gap-2">
                      <i className="ri-error-warning-line text-lg shrink-0"></i>
                      There was an error sending your message. Please try again.
                    </div>
                  )}

                  <button type="submit" disabled={isSubmitting}
                    className={`w-full text-white py-3.5 px-6 rounded-lg font-semibold transition-colors cursor-pointer whitespace-nowrap disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2 ${isMoringaOrder ? 'bg-amber-500 hover:bg-amber-600' : 'bg-green-600 hover:bg-green-700'}`}>
                    {isSubmitting ? (
                      <><i className="ri-loader-4-line animate-spin"></i>Sending...</>
                    ) : isMoringaOrder ? (
                      <><i className="ri-shopping-bag-line"></i>Place Moringa Order</>
                    ) : (
                      <><i className="ri-send-plane-line"></i>Send Message</>
                    )}
                  </button>
                </form>
              </div>

              {/* Right column — contact info */}
              <div>
                {/* Moringa order highlight */}
                <div className="bg-amber-50 border border-amber-200 rounded-2xl p-6 mb-8 flex gap-4">
                  <div className="w-14 h-14 rounded-xl overflow-hidden shrink-0">
                    <img src="https://public.readdy.ai/ai/img_res/edited_e17d2c62eadfd03030c4db97e8f52ea6_45dfadd7.jpg"
                      alt="Pure Moringa Powder" className="w-full h-full object-cover object-top" />
                  </div>
                  <div>
                    <p className="text-xs font-bold text-amber-600 uppercase tracking-wider mb-1">Now Available</p>
                    <p className="font-bold text-gray-900 text-base">Pure Moringa Powder</p>
                    <p className="text-gray-500 text-sm mt-0.5">50g · 100g · 250g · 1kg — Grown &amp; processed in Ghana</p>
                    <button
                      onClick={() => setFormData(prev => ({ ...prev, subject: 'Order Pure Moringa Powder' }))}
                      className="mt-3 inline-flex items-center text-amber-700 font-semibold text-sm hover:text-amber-900 transition-colors cursor-pointer whitespace-nowrap"
                    >
                      Order via form below <i className="ri-arrow-down-line ml-1"></i>
                    </button>
                  </div>
                </div>

                <h2 className="text-3xl font-bold text-gray-900 mb-8">Get in Touch</h2>
                <div className="space-y-6">
                  {[
                    { icon: 'ri-map-pin-line', color: 'bg-green-50 text-green-600', title: 'Visit Our Farm', content: 'MY AVA Farms\n33 Kwame Nkrumah Ave, Adabraka\nAccra, Ghana' },
                    { icon: 'ri-phone-line', color: 'bg-green-50 text-green-600', title: 'Call Us', content: '+233 23 337 1378' },
                    { icon: 'ri-mail-line', color: 'bg-green-50 text-green-600', title: 'Email Us', content: 'myavagh@gmail.com' },
                    { icon: 'ri-time-line', color: 'bg-green-50 text-green-600', title: 'Business Hours', content: 'Monday – Friday: 9:00 AM – 5:00 PM\nSaturday: 9:00 AM – 5:00 PM\nSunday: Closed' },
                  ].map((item, i) => (
                    <div key={i} className="flex items-start gap-4">
                      <div className={`w-11 h-11 flex items-center justify-center rounded-xl shrink-0 ${item.color}`}>
                        <i className={`${item.icon} text-xl`}></i>
                      </div>
                      <div>
                        <h3 className="text-base font-semibold text-gray-900 mb-1">{item.title}</h3>
                        {item.content.split('\n').map((line, j) => (
                          <p key={j} className="text-gray-500 text-sm">{line}</p>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>

                <div className="mt-10">
                  <h3 className="text-base font-semibold text-gray-900 mb-4">Find Us</h3>
                  <div className="rounded-xl overflow-hidden h-64 border border-gray-100">
                    <iframe
                      src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3970.7668!2d-0.1969!3d5.5698!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0xfdf9084b2b7a773%3A0xbed14cb2f6d7a6e!2s33%20Kwame%20Nkrumah%20Ave%2C%20Accra!5e0!3m2!1sen!2sgh!4v1234567890"
                      width="100%" height="100%" style={{ border: 0 }} allowFullScreen loading="lazy"
                      referrerPolicy="no-referrer-when-downgrade" title="MY AVA Farms Location"
                    ></iframe>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* FAQ */}
        <section className="py-20 bg-gray-50">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-12">
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">Frequently Asked Questions</h2>
              <p className="text-gray-500 text-lg">About our moringa powder, BSF products, and services</p>
            </div>
            <div className="space-y-4">
              {[
                { q: 'How do I order pure moringa powder?', a: 'Simply fill out the contact form above, select "Order Pure Moringa Powder" as the subject, choose your preferred package size and quantity, and we will confirm your order and arrange payment and delivery.' },
                { q: 'What sizes does the moringa powder come in?', a: 'Our pure moringa powder is available in 50g, 100g, 250g, and 1kg packs. Bulk and wholesale orders are also available on request.' },
                { q: 'Is the moringa powder available for delivery outside Accra?', a: 'Yes! We deliver across Ghana and can arrange international export orders. Select your delivery preference when filling in the order form.' },
                { q: 'What is Black Soldier Fly farming?', a: 'Black Soldier Fly farming is a sustainable method of converting organic waste into high-quality protein feed and organic fertilizer using BSF larvae. It supports circular economy practices.' },
                { q: 'How long does the BSF training program take?', a: 'Our BSF training programs range from 1-day intensive workshops to 4-week detailed courses, depending on your experience level and specific needs.' },
                { q: 'What products do you export?', a: 'We export BSF larvae, dried BSF protein meal, organic fertilizer, pure moringa powder, and BSF farming equipment to countries across West Africa and beyond.' },
              ].map((faq, i) => (
                <div key={i} className="bg-white p-6 rounded-xl border border-gray-100">
                  <h3 className="text-base font-semibold text-gray-900 mb-2 flex items-start gap-2">
                    <i className="ri-question-line text-green-500 mt-0.5 shrink-0"></i>{faq.q}
                  </h3>
                  <p className="text-gray-500 text-sm leading-relaxed pl-6">{faq.a}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

      </div>
    </>
  );
}
